/**
 * Usage Credits (UC) — the composite usage meter (ADR-0149).
 *
 * The unit has been renamed twice and older documents use the older names:
 * Sync Unit (SU) → Compute Unit (CU) (ADR-0317) → Usage Credit (UC) (ADR-0503,
 * which renamed this module from `compute-units.ts`). Only the LABEL moved —
 * the weights, the anchor, the rates and both invariants below are byte-identical
 * across all three names. Note that the persisted `compute_unit` meter kind in
 * Postgres, and the `compute_units` turn-stream wire value, deliberately keep the old
 * spelling; see ADR-0503 § "What is deliberately NOT renamed".
 *
 * One billable currency. Every billable activity a tenant drives converts to
 * UC via a weighted sum:
 *
 *     UC = token_UC + platform_UC
 *     token_UC    = managed_token_COGS_usd ÷ UC_TOKEN_COGS_ANCHOR_USD   (0 under BYOK)
 *     platform_UC = messages·2 + mcpCalls·2 + endpointCalls·1 + insights·5
 *
 * Buyers see UC (with a published weight table + a live gauge); tiers headline
 * an approximate "≈ N interactions/mo". "Conversations" is the chat thread, never
 * the billing unit (R-1, ADR-0149).
 *
 * **The governing invariant — every UC sells for ≥ 5× its own COGS ⇔ ≥ 80%
 * gross margin.** Because `token_UC` is COGS-PROPORTIONAL (cost ÷ a fixed
 * anchor), the token-side margin is invariant to model and conversation size:
 * at the deepest rate sold (the Enterprise floor, pinned to exactly 5× the
 * anchor) a pure-token turn lands at exactly 80%; real turns also carry
 * near-99%-margin platform UC, so managed margin is always > 80% (82–90% real).
 * Under BYOK the token COGS is the customer's, so `token_UC = 0` and only the
 * platform UC bills — making BYOK billing scale with activity (the 30K-vs-30M
 * requirement) at ~99% margin. See `usage-credits.test.ts` for the executable
 * proofs of both invariants.
 *
 * Pure module: no I/O, no env, no clock — the weights/rates are a flat data
 * table so there's one auditable place to recalibrate. **All numbers are
 * provisional, locked for launch, recalibrated against real COGS telemetry at
 * the first ~10 paying tenants** (`docs/pricing/composite-usage-model.md` §8/§10).
 * The STRUCTURE + the invariant are guaranteed by construction, not knobs.
 */

export type PricingTierId = 'starter' | 'growth' | 'scale' | 'enterprise';

/**
 * Managed token-UC anchor: `token_UC = token_COGS_usd ÷ anchor`. Pinned so the
 * deepest rate sold (`UC_FLOOR_RATE_USD`) is exactly 5× this — the margin floor.
 */
export const UC_TOKEN_COGS_ANCHOR_USD = 0.0016;

/** Platform-activity weights (value-priced; COGS is sub-cent → ~99% margin). */
export const UC_PER_MESSAGE = 2;
export const UC_PER_MCP_CALL = 2;
export const UC_PER_ENDPOINT_CALL = 1;
export const UC_PER_INSIGHT = 5;
/** SYN-565 / ENR-4 — hosted catalog enrichment, per enriched capability. */
export const UC_PER_ENRICHMENT = 1;

/**
 * SYN-565 / ENR-4 — the DEDICATED monthly enrichment allowance (enrichment-UC
 * per tenant per calendar month), ADR-0159 Acceptance record: hosted
 * enrichment is bundled into the subscription and NEVER draws from the chat
 * `compute_unit` allowance; this dedicated cap (+ the single-run lock + the
 * content-hash dedupe) is the abuse ceiling. Sized to cover one full
 * max-catalog (5,000-cap) first enrichment (~2.2 token-UC + 1 platform-UC per
 * capability ≈ 16,000 UC) plus re-run headroom. Flat across tiers (the
 * funding is per-subscription, not per-tier). Operator-adjustable here
 * without a schema change.
 */
export const ENRICHMENT_UC_MONTHLY_ALLOWANCE = 20_000;

/**
 * ADR-0442 § Phase 4 — the DEDICATED monthly allowance for background ANALYSIS
 * (conversation insights, the nightly digest, per-user learning extraction).
 *
 * Same reasoning as the enrichment allowance above, applied to the other class
 * of work nobody asked for: it is bundled into the subscription, it is not an
 * interaction, and under posted pricing it must not draw from the interaction
 * meter — a customer reconciling their usage gauge against their conversation
 * log has to find every unit accounted for, and nightly analysis would be units
 * with no interaction behind them.
 *
 * Sized against the measured shape of the work it funds: an insight run and a
 * digest run are each a handful of cheap cached-Haiku calls, and learning
 * extraction is one call per active member. 40,000 UC covers a busy workspace's
 * month with headroom, and — like enrichment — it is flat across tiers, because
 * the funding is per-subscription rather than per-tier. Operator-adjustable here
 * without a schema change.
 */
export const ANALYSIS_UC_MONTHLY_ALLOWANCE = 40_000;

/** The minimum gross margin the invariant guarantees on every UC sold. */
export const UC_MARGIN_FLOOR = 0.8;
/** Every UC sells for ≥ this multiple of its COGS. `1/(1−0.8) = 5`. */
export const UC_MIN_COGS_MULTIPLE = 5;

/** Pure pay-as-you-go / overage rate — the most expensive $/UC (no commitment). */
export const UC_PAYG_RATE_USD = 0.012;

/**
 * Effective included $/UC by tier — the discount a committed tier buys. Falls
 * as tiers rise (bigger tier = better rate) and stays ≥ the floor; every tier
 * is cheaper per UC than pure PAYG.
 */
export const UC_INCLUDED_RATE_USD: Readonly<Record<PricingTierId, number>> = {
  starter: 0.01,
  growth: 0.0091,
  scale: 0.0083,
  enterprise: 0.008,
};

/**
 * The deepest rate sold (Enterprise floor). Pinned to exactly
 * `UC_MIN_COGS_MULTIPLE × UC_TOKEN_COGS_ANCHOR_USD` — the structural margin
 * floor. The MANAGED Enterprise rate is never negotiated below this (BYOK may
 * go lower: no token COGS → still ~99% margin).
 */
export const UC_FLOOR_RATE_USD = UC_INCLUDED_RATE_USD.enterprise;

export interface TurnUsageCreditsInput {
  /**
   * Measured managed LLM token COGS for the turn, USD (from `cost_events` /
   * `computeCostUsd`). Ignored when `byok` is true.
   */
  readonly tokenCostUsd: number;
  /** Assistant messages / turns in this unit of work. Default 0. */
  readonly messages?: number;
  /** MCP `tools/call` invocations. Default 0. */
  readonly mcpCalls?: number;
  /** Customer endpoint calls (Channel B execution). Default 0. */
  readonly endpointCalls?: number;
  /** Insights / analytics operations. Default 0. */
  readonly insights?: number;
  /** Hosted catalog-enrichment capability calls (SYN-565 / ENR-4). Default 0. */
  readonly enrichments?: number;
  /**
   * Customer brought their own LLM key — Syncanix bears no token COGS, so the
   * token component is 0. Platform UC still accrues. Default false (managed).
   */
  readonly byok?: boolean;
}

function assertNonNegative(field: string, value: number, integer: boolean): void {
  if (!Number.isFinite(value) || value < 0 || (integer && !Number.isInteger(value))) {
    throw new RangeError(
      `${field} must be a ${integer ? 'non-negative integer' : 'non-negative number'}, got ${String(
        value,
      )}`,
    );
  }
}

/**
 * Total Usage Credits consumed by one unit of work (a turn, or an aggregate).
 * Pure + total apart from input validation. Returns a precise (possibly
 * fractional) UC value — round only at the billing/display boundary.
 */
export function computeTurnUsageCredits(input: TurnUsageCreditsInput): number {
  assertNonNegative('tokenCostUsd', input.tokenCostUsd, false);
  const messages = input.messages ?? 0;
  const mcpCalls = input.mcpCalls ?? 0;
  const endpointCalls = input.endpointCalls ?? 0;
  const insights = input.insights ?? 0;
  const enrichments = input.enrichments ?? 0;
  assertNonNegative('messages', messages, true);
  assertNonNegative('mcpCalls', mcpCalls, true);
  assertNonNegative('endpointCalls', endpointCalls, true);
  assertNonNegative('insights', insights, true);
  assertNonNegative('enrichments', enrichments, true);

  const tokenUc = input.byok === true ? 0 : input.tokenCostUsd / UC_TOKEN_COGS_ANCHOR_USD;
  const platformUc =
    messages * UC_PER_MESSAGE +
    mcpCalls * UC_PER_MCP_CALL +
    endpointCalls * UC_PER_ENDPOINT_CALL +
    insights * UC_PER_INSIGHT +
    enrichments * UC_PER_ENRICHMENT;
  return tokenUc + platformUc;
}

/**
 * Gross margin for selling `uc` Usage Credits at `ratePerUc` against a turn's
 * `turnCogsUsd`. Returns 0 when no UC is sold (no revenue). Pure.
 */
export function grossMarginAtRate(turnCogsUsd: number, uc: number, ratePerUc: number): number {
  const revenue = uc * ratePerUc;
  if (revenue <= 0) {
    return 0;
  }
  return (revenue - turnCogsUsd) / revenue;
}
